import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { getAvatarGradientClass, getAvatarInitials } from './avatar'

type ToastVariant = 'success' | 'error' | 'info' | 'warning'

type ToastPosition = 'top-right' | 'bottom-left'

type ToastInput = {
  title?: string
  message: string
  variant?: ToastVariant
  durationMs?: number
  persistent?: boolean
  position?: ToastPosition
  showProgress?: boolean
  saveToNotifications?: boolean
  iconUrl?: string
  avatarUrl?: string
  productImageUrl?: string
  customerInitials?: string
}

type ToastNotification = {
  id: string
  title?: string
  message: string
  variant: ToastVariant
  createdAt: number
  read: boolean
  iconUrl?: string
  avatarUrl?: string
  productImageUrl?: string
  customerInitials?: string
}

type VisibleToast = ToastNotification & {
  durationMs: number
  position: ToastPosition
  showProgress: boolean
}

type ToastContextValue = {
  toasts: VisibleToast[]
  toast: (input: ToastInput) => string
  dismissToast: (id: string) => void
  notifications: ToastNotification[]
  unreadCount: number
  markAllRead: () => void
  clearNotifications: () => void
  notificationsOpen: boolean
  setNotificationsOpen: (open: boolean) => void
  toggleNotifications: () => void
}

const STORAGE_KEY = 'digigo.notifications.v1'

function isSocialProofPurchaseNotification(notification: { title?: string; message: string }) {
  const normalizedTitle = String(notification.title || '').trim().toLowerCase()
  return normalizedTitle.endsWith('vừa mua') && notification.message.includes('•')
}

function safeParseNotifications(value: string | null): ToastNotification[] {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((x) => {
        if (!x || typeof x !== 'object') return null
        const id = String((x as any).id || '')
        if (!id) return null
        const variant = String((x as any).variant || 'info') as ToastVariant
        return {
          id,
          title: typeof (x as any).title === 'string' ? (x as any).title : undefined,
          message: String((x as any).message || ''),
          variant: (variant === 'success' || variant === 'error' || variant === 'warning' || variant === 'info') ? variant : 'info',
          createdAt: Number((x as any).createdAt) || Date.now(),
          read: Boolean((x as any).read),
          iconUrl: typeof (x as any).iconUrl === 'string' ? (x as any).iconUrl : undefined,
          avatarUrl: typeof (x as any).avatarUrl === 'string' ? (x as any).avatarUrl : undefined,
          productImageUrl: typeof (x as any).productImageUrl === 'string' ? (x as any).productImageUrl : undefined,
          customerInitials: typeof (x as any).customerInitials === 'string' ? (x as any).customerInitials : undefined,
        } as ToastNotification
      })
      .filter((x): x is ToastNotification => Boolean(x))
      .filter((x) => !isSocialProofPurchaseNotification(x))
  } catch {
    return []
  }
}

function generateId() {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`
  }
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<VisibleToast[]>([])
  const [notifications, setNotifications] = useState<ToastNotification[]>(() => {
    try {
      return safeParseNotifications(localStorage.getItem(STORAGE_KEY))
    } catch {
      return []
    }
  })
  const [notificationsOpen, setNotificationsOpen] = useState(false)

  const timersRef = useRef<Record<string, number>>({})

  const persistNotifications = useCallback((next: ToastNotification[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    persistNotifications(notifications)
  }, [notifications, persistNotifications])

  useEffect(() => {
    return () => {
      const timers = timersRef.current
      Object.values(timers).forEach((t) => window.clearTimeout(t))
      timersRef.current = {}
    }
  }, [])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timers = timersRef.current
    if (timers[id]) {
      window.clearTimeout(timers[id])
      delete timers[id]
    }
  }, [])

  const toast = useCallback((input: ToastInput) => {
    const id = generateId()
    const variant: ToastVariant = input.variant || 'info'
    const createdAt = Date.now()
    const saveToNotifications = input.saveToNotifications ?? true

    const notif: ToastNotification = {
      id,
      title: input.title,
      message: input.message,
      variant,
      createdAt,
      read: false,
      iconUrl: input.iconUrl,
      avatarUrl: input.avatarUrl,
      productImageUrl: input.productImageUrl,
      customerInitials: input.customerInitials,
    }

    if (saveToNotifications) {
      setNotifications((prev) => {
        const next = [notif, ...prev].slice(0, 60)
        return next
      })
    }

    if (!input.persistent) {
      const durationMs = Math.max(1200, Math.min(12_000, input.durationMs ?? 4200))
      const position: ToastPosition = input.position ?? 'top-right'
      const showProgress = input.showProgress ?? false
      const vt: VisibleToast = { ...notif, durationMs, position, showProgress }
      setToasts((prev) => [vt, ...prev].slice(0, 5))
      timersRef.current[id] = window.setTimeout(() => dismissToast(id), durationMs)
    }

    return id
  }, [dismissToast])

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }, [])

  const clearNotifications = useCallback(() => {
    setNotifications([])
  }, [])

  const toggleNotifications = useCallback(() => {
    setNotificationsOpen((prev) => !prev)
  }, [])

  useEffect(() => {
    if (!notificationsOpen) return
    markAllRead()
  }, [notificationsOpen, markAllRead])

  const unreadCount = useMemo(() => notifications.reduce((acc, n) => acc + (n.read ? 0 : 1), 0), [notifications])

  const value = useMemo<ToastContextValue>(() => {
    return {
      toasts,
      toast,
      dismissToast,
      notifications,
      unreadCount,
      markAllRead,
      clearNotifications,
      notificationsOpen,
      setNotificationsOpen,
      toggleNotifications,
    }
  }, [toasts, toast, dismissToast, notifications, unreadCount, markAllRead, clearNotifications, notificationsOpen, toggleNotifications])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport />
      <NotificationsPanel />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

function variantClasses(variant: ToastVariant) {
  if (variant === 'success') return 'border-emerald-200 bg-emerald-50 text-emerald-900'
  if (variant === 'error') return 'border-red-200 bg-red-50 text-red-900'
  if (variant === 'warning') return 'border-amber-200 bg-amber-50 text-amber-900'
  return 'border-blue-200 bg-blue-50 text-blue-900'
}

function ToastViewport() {
  const { toasts, dismissToast } = useToast()

  const topRightToasts = toasts.filter((t) => t.position === 'top-right')
  const bottomLeftToasts = toasts.filter((t) => t.position === 'bottom-left')

  return (
    <>
      {/* Top-right toasts */}
      <div className="fixed top-4 right-4 z-[60] flex flex-col gap-2 w-[min(360px,calc(100vw-2rem))]">
        {topRightToasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismissToast} />
        ))}
      </div>

      {/* Bottom-left toasts */}
      <div className="hidden md:flex fixed bottom-4 left-4 z-[60] flex-col gap-2 w-[min(360px,calc(100vw-2rem))]">
        {bottomLeftToasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismissToast} />
        ))}
      </div>
    </>
  )
}

function ToastItem({ toast: t, onDismiss }: { toast: VisibleToast; onDismiss: (id: string) => void }) {
  const isBottomLeft = t.position === 'bottom-left'
  const avatarUrl = t.avatarUrl || t.iconUrl
  const [avatarFailed, setAvatarFailed] = useState(false)
  const [avatarSeed] = useState(() => Date.now())
  const [isEffectsOn, setIsEffectsOn] = useState(() => {
    try {
      return window.localStorage.getItem('digigo-effects') === 'on'
    } catch {
      return false
    }
  })
  const avatarSeedText = t.customerInitials || t.title || t.message
  const initials = getAvatarInitials(avatarSeedText)
  const avatarGradientClass = getAvatarGradientClass(avatarSeedText, avatarSeed)

  useEffect(() => {
    setAvatarFailed(false)
  }, [avatarUrl])

  useEffect(() => {
    const onEffectsChanged = (ev: Event) => {
      const next = (ev as CustomEvent<{ isEffectsOn: boolean }>).detail?.isEffectsOn
      if (typeof next === 'boolean') setIsEffectsOn(next)
    }
    window.addEventListener('digigo-effects-changed', onEffectsChanged)
    return () => window.removeEventListener('digigo-effects-changed', onEffectsChanged)
  }, [])

  if (isBottomLeft) {
    const toastSurfaceClass = isEffectsOn
      ? 'border-2 border-amber-400/70 dark:border-amber-500/50 bg-gradient-to-br from-slate-900/95 via-slate-800/95 to-slate-900/95 text-slate-100'
      : 'border-2 border-amber-400/80 dark:border-amber-500/60 bg-gradient-to-br from-white/95 via-slate-50/95 to-slate-100/90 text-slate-900'
    const closeBtnClass = isEffectsOn
      ? 'hover:bg-white/15 text-white/90'
      : 'hover:bg-slate-900/10 text-slate-500'
    const avatarBorderClass = isEffectsOn ? 'border-white/20' : 'border-slate-300/60'
    const avatarImageBgClass = isEffectsOn ? 'bg-white/15' : 'bg-white/70'
    const initialsClass = isEffectsOn ? 'text-white' : 'text-slate-900'
    const titleClass = isEffectsOn ? 'text-white' : 'text-slate-900'
    const messageClass = isEffectsOn ? 'text-slate-200/90' : 'text-slate-700'
    const productShellClass = isEffectsOn
      ? 'border-white/20 bg-white/10'
      : 'border-slate-300/60 bg-white/70'
    const productTextClass = isEffectsOn ? 'text-white/75' : 'text-slate-500'

    return (
      <div className={`rounded-xl shadow-xl overflow-hidden backdrop-blur-md ${toastSurfaceClass}`}>
        <div className="relative p-3 pr-9">
          <button
            type="button"
            onClick={() => onDismiss(t.id)}
            className={`absolute top-2 right-2 shrink-0 p-1 rounded ${closeBtnClass}`}
            aria-label="Đóng"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-3">
            {/* Avatar tròn - chữ cái đầu mỗi từ */}
            <div
              className={`h-10 w-10 shrink-0 rounded-full border ${avatarBorderClass} flex items-center justify-center text-xs font-bold overflow-hidden ${avatarUrl && !avatarFailed ? avatarImageBgClass : `bg-gradient-to-br ${avatarGradientClass}`}`}
            >
              {avatarUrl && !avatarFailed ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                <span className={initialsClass}>{initials}</span>
              )}
            </div>

            <div className="min-w-0 flex-1">
              {t.title ? <div className={`text-sm font-semibold leading-5 truncate ${titleClass}`}>{t.title}</div> : null}
              <div className={`text-sm leading-5 break-words ${messageClass}`}>{t.message}</div>
            </div>

            <div className={`w-24 shrink-0 rounded-md overflow-hidden border ${productShellClass}`}>
              <div className="aspect-video w-full">
                {t.productImageUrl ? (
                  <img src={t.productImageUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className={`h-full w-full flex items-center justify-center text-[10px] font-semibold ${productTextClass}`}>
                    PRODUCT
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {t.showProgress && (
          <div className="h-1 w-full bg-amber-100/70 dark:bg-amber-300/15">
            <div
              className="h-full bg-gradient-to-r from-yellow-300 via-amber-400 to-yellow-500 animate-shrink-width"
              style={{ animationDuration: `${t.durationMs}ms` }}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={`border rounded-lg shadow-lg overflow-hidden ${variantClasses(t.variant)}`}>
      <div className="p-3">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            {t.title ? <div className="text-sm font-semibold leading-5 truncate">{t.title}</div> : null}
            <div className="text-sm leading-5 break-words">{t.message}</div>
          </div>
          <button
            type="button"
            onClick={() => onDismiss(t.id)}
            className="shrink-0 p-1 rounded hover:bg-black/5"
            aria-label="Đóng"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      {t.showProgress && (
        <div className="h-1 w-full bg-gray-200/50">
          <div
            className="h-full bg-gradient-to-r from-yellow-300 via-amber-400 to-yellow-500 animate-shrink-width"
            style={{ animationDuration: `${t.durationMs}ms` }}
          />
        </div>
      )}
    </div>
  )
}

function NotificationsPanel() {
  const { notificationsOpen, setNotificationsOpen, notifications, clearNotifications } = useToast()

  if (!notificationsOpen) return null

  return (
    <div className="fixed inset-0 z-[55]">
      <div className="absolute inset-0 bg-black/30" onClick={() => setNotificationsOpen(false)} />
      <div className="absolute top-16 right-4 w-[min(420px,calc(100vw-2rem))] max-h-[70vh] rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-xl overflow-hidden">
        <div className="h-12 px-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-800">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Thông báo</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={clearNotifications}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Xoá
            </button>
            <button
              type="button"
              onClick={() => setNotificationsOpen(false)}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Đóng
            </button>
          </div>
        </div>

        <div className="p-2 overflow-auto max-h-[calc(70vh-3rem)]">
          {notifications.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 p-3">Chưa có thông báo.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  className={`border rounded-lg p-3 ${variantClasses(n.variant)} ${n.read ? 'opacity-80' : ''}`}
                >
                  {n.title ? <div className="text-sm font-semibold">{n.title}</div> : null}
                  <div className="text-sm">{n.message}</div>
                  <div className="text-[11px] mt-1 text-gray-600/80 dark:text-gray-300/70">
                    {new Date(n.createdAt).toLocaleString('vi-VN')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
